import { api } from '../../lib/api'

export type EtkinKaynak = 'analiz' | 'tesis' | 'lit'
export type VeriGuveni = 'olcum' | 'kurum' | 'celisik'
export type StokTuru = 'hammadde' | 'urun' | 'sarf' | 'yedek' | 'yakit'
export type MetanKaynagi = 'bmp' | 'ch4'

export type DegerCift = { deger: number | null; kaynak: EtkinKaynak | null }

export type StokKartiOzet = {
  id: string
  kod: string
  ad: string
  tur: StokTuru
  grup: string | null
  birim: string
  hij: boolean
  km: number | null
  okmKm: number | null
  verimNlKgOkm: number | null
  ch4Oran: number | null
  kritikSeviye: number | null
  havuzBakiye: number | null
  /**
   * Güncel stok — defterin son `bakiye_sonrasi` anlık görüntüsü (§5.1'de kartın
   * bakiye sütunu yok). Defterde izi olmayan kartta null: 0 basmak "stoğu bitti"
   * demek olurdu (K32).
   */
  mevcutStok: number | null
  /** Son 30 günün çıkış toplamı; defterde izi olmayan kartta null. */
  aylikTuketim: number | null
  durum: 'aktif' | 'pasif'
  veriGuven: VeriGuveni | null
  inhibisyonRiski: string | null
}

/** Ekran 01 panelindeki "Son Hareketler" mini listesinin satırı. */
export type KartHareketi = {
  zaman: string
  belgeNo: string
  etiket: string
  /** -1 çıkış · 0 düzeltme · +1 giriş — `.vv plus/minus` sınıfını seçer. */
  yon: number
  miktar: number
  birim: string
}

/**
 * Ekran 01 hammadde panelindeki "Stok & Son Analizler" satırı. Analiz karta
 * numune → lot → firma×ürün havuzu zinciriyle bağlanır (K26), o yüzden ayrı uç.
 */
export type KartAnalizi = {
  zaman: string
  numuneNo: string
  km: number | null
}

export type StokKarti = StokKartiOzet & {
  kaynak: 'literatur' | 'ozel' | 'sistem'
  yogunluk: number | null
  ph: number | null
  cnOrani: number | null
  toplamAzotTkn: number | null
  amonyumAzotu: number | null
  fosforP2o5: number | null
  potasyumK2o: number | null
  kukurtSKm: number | null
  yagKm: number | null
  proteinKm: number | null
  ligninKm: number | null
  metanVerimiBmp: number | null
  bozunmaOrani: number | null
  minBeklemeGun: number | null
  maksBeslemeOraniOkm: number | null
  onIslem: string | null
  monoBeslenebilir: boolean
  abpKategorisi: string | null
  atikKoduEwc: string | null
  mevsimsellik: string | null
  depolamaSekli: string | null
  depolamaOmruGun: number | null
  veriKaynagi: string | null
  tesisDegerleri: Record<string, number>
}

export type Teorik = {
  miktarT: number
  kmKutle: number | null
  okm: number | null
  biyogaz: number | null
  metan: number | null
  metanKaynagi: MetanKaynagi | null
  elektrik: number | null
  isi: number | null
  km: DegerCift
  okmYuzde: DegerCift
  verim: DegerCift
  ch4: DegerCift
}

export type TurSayaci = { tur: StokTuru; adet: number }

export type KartOzeti = {
  toplamKart: number
  aktifHammadde: number
  /**
   * Güncel stoğa göre sayılır; defterde izi olmayan kart hiçbir sayıma girmez.
   * Tip yine nullable: stok kaynağı yoksa 0 değil "—" gösterilmeli (K32).
   */
  kritikAlti: number | null
  stoguTukenen: number | null
  hijyenGerekli: number
}

export type CariOzet = {
  id: string
  kod: string
  unvan: string
  tel: string | null
  eposta: string | null
  adres: string | null
  vergiNo: string | null
  rolAtik: boolean
  rolTedarikci: boolean
  rolNakliyeci: boolean
  durum: 'aktif' | 'pasif'
  /** Girişi olan havuzlarından en az biri analizsiz — firma hücresindeki .warn-i üçgeni. */
  analizsizHavuzVar: boolean
  /** Tonaj ağırlıklı KM sapması (%). */
  kmSapmaOrt: number | null
  /** Baskın role göre özet — mal kabul/filo (Faz 2/5) gelene kadar null. */
  buAy: string | null
  sonIslem: string | null
  bakiye: number | null
}

export type Cari = CariOzet & { yetkili: string | null; vadeGun: number | null }

export type KmSapmaSatiri = {
  stokKartiId: string
  stokKod: string
  stokAd: string
  literaturKm: number | null
  etkinKm: number | null
  sapmaYuzde: number | null
  analizSayisi: number
  etkinKaynak: EtkinKaynak
}

/** Ekran 04 tam sayfa "İşlem Akışı" satırı — defter, havuz üzerinden firmaya bağlı (K26). */
export type CariIslemSatiri = {
  zaman: string
  tip: string
  referans: string
  /** Stok kartının adı; miktar/birim ayrı gelir ki biçimleme trFormat'ta kalsın. */
  detay: string
  /** -1 çıkış · 0 düzeltme · +1 giriş. */
  yon: number
  miktar: number
  birim: string
  /** K12: birim fiyat opsiyonel — fiyatsız harekette null. */
  tutar: number | null
}

export type CariDetay = {
  cari: Cari
  kmKarnesi: KmSapmaSatiri[]
  kmSapmaOrt: number | null
  islemler: CariIslemSatiri[]
}

export type Havuz = {
  id: string
  cariId: string
  cariKod: string
  cariUnvan: string
  stokKartiId: string
  stokKod: string
  stokAd: string
  bakiye: number
  haoBirimDeger: number | null
  etkinKm: DegerCift
  etkinOkm: DegerCift
  analizSayisi: number
  planMi: boolean
}

export const stokKartlari = {
  liste: (tur?: StokTuru, q?: string) => {
    const p = new URLSearchParams()
    if (tur) p.set('tur', tur)
    if (q) p.set('q', q)
    return api<StokKartiOzet[]>(`/api/stok-kartlari?${p}`)
  },
  sayaclar: () => api<TurSayaci[]>('/api/stok-kartlari/sayaclar'),
  ozet: () => api<KartOzeti>('/api/stok-kartlari/ozet'),
  detay: (anahtar: string) => api<StokKarti>(`/api/stok-kartlari/${anahtar}`),
  teorik: (anahtar: string) => api<Teorik>(`/api/stok-kartlari/${anahtar}/teorik`),
  /** Panelin "Son Hareketler" listesi — defterden kart kimliğiyle süzülmüş son satırlar. */
  hareketler: (anahtar: string, adet = 3) =>
    api<KartHareketi[]>(`/api/stok-kartlari/${anahtar}/hareketler?adet=${adet}`),
  /** Hammadde panelinin "Stok & Son Analizler" listesi — kartın son lot analizleri. */
  analizler: (anahtar: string, adet = 2) =>
    api<KartAnalizi[]>(`/api/stok-kartlari/${anahtar}/analizler?adet=${adet}`),
  tesisDegerleri: (id: string, degerler: Record<string, number | null>) =>
    api<StokKarti>(`/api/stok-kartlari/${id}/tesis-degerleri`, {
      yontem: 'PUT',
      govde: { degerler },
    }),
  sonrakiKod: (tur: StokTuru) =>
    api<{ kod: string }>(`/api/stok-kartlari/sonraki-kod?tur=${tur}`),
  /**
   * Kartı günceller. Sunucu PUT'ta 27 alanın TAMAMINI bekliyor
   * (StokKartiIstek pozisyonel record); bu yüzden çağıran mevcut kartın
   * üzerine yalnız değişenleri yazıp tamamını gönderiyor.
   */
  kaydet: (id: string, govde: Partial<StokKarti> & { kod: string; ad: string; tur: StokTuru }) =>
    api<StokKarti>(`/api/stok-kartlari/${id}`, { yontem: 'PUT', govde }),

  olustur: (govde: Partial<StokKarti> & { kod: string; ad: string; tur: StokTuru }) =>
    api<StokKarti>('/api/stok-kartlari', { yontem: 'POST', govde }),

  kopyala: (id: string, kod: string, ad: string) =>
    api<StokKarti>(`/api/stok-kartlari/${id}/kopyala`, {
      yontem: 'POST',
      govde: { kod, ad },
    }),
}

export type CariOzeti = {
  toplamFirma: number
  atikSaglayici: number
  tedarikci: number
  nakliyeci: number
  /** Mal kabul kayıtları (Faz 2) gerektirir. */
  buAyHammaddeT: number | null
}

export const cariler = {
  ozet: () => api<CariOzeti>('/api/cariler/ozet'),
  liste: (q?: string, rol?: string) => {
    const p = new URLSearchParams()
    if (q) p.set('q', q)
    if (rol) p.set('rol', rol)
    return api<CariOzet[]>(`/api/cariler?${p}`)
  },
  detay: (id: string) => api<CariDetay>(`/api/cariler/${id}`),
  kaydet: (id: string | null, govde: Partial<Cari>) =>
    api<Cari>(id ? `/api/cariler/${id}` : '/api/cariler', {
      yontem: id ? 'PUT' : 'POST',
      govde,
    }),
}

export const havuzlar = {
  liste: (filtre: { q?: string; cariId?: string; stokId?: string } = {}) => {
    const p = new URLSearchParams()
    if (filtre.q) p.set('q', filtre.q)
    if (filtre.cariId) p.set('cariId', filtre.cariId)
    if (filtre.stokId) p.set('stokId', filtre.stokId)
    return api<Havuz[]>(`/api/havuzlar?${p}`)
  },

  /** K27: girişsiz eşleşme için plan havuzu açar (varsa mevcudu döner). */
  plan: (cariId: string, stokKartiId: string) =>
    api<Havuz>('/api/havuzlar/plan', { yontem: 'POST', govde: { cariId, stokKartiId } }),
}
